import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";

dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Accepted timestamp inputs. The read layer normalizes Firestore `Timestamp`s to ISO
 * strings (see firestore/hooks.ts), so values are normally strings — but we also tolerate
 * Date and the raw Firestore `{seconds, nanoseconds}` shape defensively, and NEVER return a
 * non-string (a raw object reaching React throws "Objects are not valid as a React child").
 */
export type DateInput =
  | string
  | number
  | Date
  | { seconds: number; nanoseconds?: number }
  | null
  | undefined;

function toDayjs(value: DateInput): Dayjs | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "object" && !(value instanceof Date) && "seconds" in value) {
    return dayjs.unix(value.seconds);
  }
  const d = dayjs(value as string | number | Date);
  return d.isValid() ? d : null;
}

/** Absolute UTC timestamp, e.g. "2026-06-14 09:42:01 UTC". Returns "—" if unparseable. */
export function formatTimestamp(value: DateInput): string {
  const d = toDayjs(value);
  return d ? d.utc().format("YYYY-MM-DD HH:mm:ss [UTC]") : "—";
}

/** Relative time, e.g. "3 minutes ago" / "in 2 hours". Returns "—" if unparseable. */
export function formatRelative(value: DateInput): string {
  const d = toDayjs(value);
  return d ? d.fromNow() : "—";
}

/** True when the timestamp is in the past. */
export function isPast(value: DateInput): boolean {
  const d = toDayjs(value);
  return d ? d.isBefore(dayjs()) : false;
}

/** True when the timestamp falls within the next `withinMs` window (and is still in the future). */
export function isSoon(value: DateInput, withinMs: number): boolean {
  const d = toDayjs(value);
  if (!d) return false;
  const delta = d.diff(dayjs());
  return delta >= 0 && delta <= withinMs;
}
